import {
  useAppDispatch,
  useAppSelector,
  useEffect,
  useRef,
  useState,
} from "@/utils/hooks/reactUseCustomHooks";
import { setPreferences } from "@/utils/redux/recipes/recipeSlice/actions";
import { useFilter, usePagination } from "@dxp-feed/algolia";

import { useGigya, useGigyaAccount } from "@dxp-feed/gigya";
import { getFromLocalStorage } from "@/helpers/storageHelper";
import { updateParams } from "@/utils/onetrust";
import { USER_DIETARY_PREFERENCE_KEYS } from "@/utils/constant";
import { mulesoftValidateMember } from "@/services/mulesoft";

export const CheckValidProductImage = (images: string[]): string | false => {
  if (!images?.length) return false;

  const image = images.find((image) => {
    if (!image?.length || !image.includes("/original/")) return false;
    try {
      new URL(image);
      return true;
    } catch {
      return false;
    }
  });
  return image ?? false;
};

export const GetUniqueOriginalProductImages = (images: string[]): string[] => {
  if (!images?.length) return [];
  const unique = new Set<string>();
  for (const image of images) {
    if (image.includes("/original/") && image.length) {
      try {
        new URL(image);
        unique.add(image);
      } catch {
        // ignore invalid URLs
      }
    }
  }
  return Array.from(unique);
};

export const CheckIfRecordIsPreferred = (
  type: "product" | "recipe" | "article",
  record: any
): boolean => {
  const { loggedIn: userLoggedIn, lifestyles } = useAppSelector(
    (state) => state.gigyaslice
  );

  // Corrected the mapping structure
  const recipeArticlePreferenceMapping: Record<string, string> = {
    glutenFree: "Gluten Free",
    vegan: "Vegan",
    lactoseFree: "Lactose Free",
    vegetarian: "Vegetarian",
    kosher: "Kosher",
  };

  if (!userLoggedIn || !lifestyles) return false;

  // Extract dietary preferences based on user lifestyle selections
  const selectedPreferences = USER_DIETARY_PREFERENCE_KEYS.filter(
    (preference) => lifestyles[preference]
  );

  if (selectedPreferences.length === 0) return false;

  switch (type) {
    case "product":
      return selectedPreferences.some(
        (preference) => record?.allergens?.[preference]
      );
    case "recipe":
      return selectedPreferences.some((preference) =>
        record?.recipeTags?.includes(recipeArticlePreferenceMapping[preference])
      );
    case "article":
      return selectedPreferences.some((preference) =>
        record?.category?.includes(recipeArticlePreferenceMapping[preference])
      );
    default:
      return false;
  }
};

export const SanitizeGlobalSearchInput = (string: string): string => {
  return (
    string
      .replace(/<\/?[^>]+(>|$)/g, "")
      .replace(/[<>%;{}'"=/$!@#^*[\]()+\\?]/g, "")
      .replace(
        /([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g,
        ""
      ) // Remove emojis
      // eslint-disable-next-line no-misleading-character-class
      .replace(/[\u200D\uFE0F]/g, "") // Remove invisible joiners/selectors
      .trimStart()
  );
};

export const TextToSlug = (str: string): string => {
  return str
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\wÀ-ÿ-]+/g, "");
};

export const minToHourConverter = (min: number): string => {
  const hours = Math.floor(min / 60);
  const minutes = min % 60;

  if (hours > 0 && minutes === 0) {
    return `${hours} h`;
  } else if (hours > 0) {
    return `${hours} h ${minutes} mins`;
  } else if (minutes > 1) {
    return `${minutes} mins`;
  } else {
    return `${minutes} min`;
  }
};

export const slugToTitle = (slug: string): string => {
  return decodeURIComponent(slug) // Decode URL-encoded strings
    .replace(/-/g, " ") // Replace hyphens with spaces
    .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize the first letter of each word
};

// Format slug
export const formatSlug = (slug: string): string => {
  if (slug === "family-friendly") return "Family-Friendly";

  if (slug.includes("--")) {
    return slug
      .replace(/--/g, " / ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  return slugToTitle(slug);
};

// Function to check if a value is NOT part of the enum
export const isNotInEnum = <T extends Record<string, string | number>>(
  enumObj: T,
  value: unknown
): boolean => {
  return !Object.values(enumObj).includes(value as T[keyof T]);
};

export const capitalizeWords = (str: string) => {
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

export const useFetchAlgoliaData = (filterAttribute: string) => {
  // Fetch filtered tags from the Algolia index based on the provided attribute
  const { items: tags, refine } = useFilter({
    attribute: filterAttribute,
    limit: 5000,
  });

  // Fetch pagination details to manage pagination state
  const pagination = usePagination();

  // Return the fetched data for use in components
  return { tags, pagination, refine };
};

// Define the loadLogin function in a way that works inside a React component
interface UseLoginReturn {
  loadLogin: (flow: string) => void;
}

export const getWeekDays = (
  fromDate: Date = new Date()
): { lastThursday: string; nextWednesday: string } => {
  const formatDate = (date: Date): string => {
    const day = date.getUTCDate();
    const month = date.toLocaleDateString("en-US", { month: "short" });
    return `${month} ${day}${getOrdinal(day)}`;
  };

  const getOrdinal = (day: number): string => {
    if (day % 10 === 1 && day % 100 !== 11) return "st";
    if (day % 10 === 2 && day % 100 !== 12) return "nd";
    if (day % 10 === 3 && day % 100 !== 13) return "rd";
    return "th";
  };

  // Create UTC-adjusted Date object for calculations
  const utcDate = new Date(
    Date.UTC(
      fromDate.getUTCFullYear(),
      fromDate.getUTCMonth(),
      fromDate.getUTCDate()
    )
  );

  // Get last Thursday
  const lastThursday = new Date(utcDate);
  const daysSinceThursday = (utcDate.getUTCDay() - 4 + 7) % 7; // Thursday is 4
  lastThursday.setUTCDate(utcDate.getUTCDate() - daysSinceThursday);

  // Get next Wednesday
  const nextWednesday = new Date(utcDate);
  const daysUntilWednesday = (10 - utcDate.getUTCDay()) % 7; // Wednesday is 3
  nextWednesday.setUTCDate(utcDate.getUTCDate() + daysUntilWednesday);

  return {
    lastThursday: formatDate(lastThursday),
    nextWednesday: formatDate(nextWednesday),
  };
};

export const useLogin = (): UseLoginReturn => {
  const { gigya, isLoaded } = useGigya();
  const { loadSSO } = useGigyaAccount({ gigya, isLoaded });
  const region = getFromLocalStorage("storeRegion");

  const loadLogin = (flow: string) => {
    if (!gigya || !isLoaded || !loadSSO) {
      console.error("Gigya context or loadSSO function is not available.");
      return;
    }

    try {
      const gaLinker = (window as any)?.decorateUrl();
      const glLinker = (window as any)?.glDecorateUrl();
      loadSSO({
        authFlow: process.env.NEXT_PUBLIC_GIGYA_LOGIN_AUTH_FLOW as string,
        context: {
          flow,
          ga: gaLinker,
          gl: updateParams(glLinker),
          region: region || (process.env.NEXT_PUBLIC_DEFAULT_REGION as string),
          brand: process.env.NEXT_PUBLIC_DEFAULT_BRAND as string,
          lang: process.env.NEXT_PUBLIC_DEFAULT_LOCALE as string,
          homeBanner: process.env.NEXT_PUBLIC_DEFAULT_HOME_BANNER as string,
          locale: process.env.NEXT_PUBLIC_DEFAULT_LOCALE as string,
        } as any,
      });
    } catch (error) {
      console.error("Error loading SSO:", error);
    }
  };

  return { loadLogin };
};

export const SpoonguruHook = () => {
  const dispatch: any = useAppDispatch();
  const { uid: Uid } = useAppSelector((state) => state.gigyaslice);
  const setPreferencesOnspoonguru = async (preferences: string[]) => {
    if (Uid) {
      dispatch(
        setPreferences({
          user_id: Uid,
          dietary_preferences: preferences,
        })
      );
    }
  };
  return { setPreferencesOnspoonguru };
};

// refineStore.ts
const refineStore: Record<string, any> = {};

/**
 * Save a refine function with an attribute key
 */
export const saveRefine = (attribute: string, refineFn: any) => {
  refineStore[attribute] = refineFn;
};

/**
 * Get a refine function by its attribute key
 */
export const getRefine = (attribute: string): any | undefined => {
  return refineStore[attribute];
};

export const parseHtml = (html: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  return doc.body.textContent ?? "";
};

// export const useDelayedCallback = (value: number, delay: number) => {
//   const [debouncedValue, setDebouncedValue] = useState(value);
//   useEffect(() => {
//     const handler = setTimeout(() => {
//       setDebouncedValue(value);
//     }, delay);

//     return () => {
//       clearTimeout(handler);
//     };
//   }, [value, delay]);

//   return debouncedValue;
// };

export const useDelayedCallback = (value: number, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  const prevValueRef = useRef<number>(0);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (prevValueRef.current != value) {
        setDebouncedValue(value);
        prevValueRef.current = value;
      } else {
        setDebouncedValue(value + 0.000001);
      }
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return Math.floor(debouncedValue);
};

export const storeInfoId = (urlLocalStoreId: any) => {
  if (urlLocalStoreId && urlLocalStoreId.match(/^0+[1-9]/)) {
    urlLocalStoreId = urlLocalStoreId.replace(/^0+/, "");
  }
  return urlLocalStoreId;
};

// Regex to remove all HTML tags
export const removeHTMLTags = (str: string) => {
  return str?.replace(/<[^>]*>/g, "").trim();
};
/***
 * Change the points format for en and fr
 */

export const changePointsFormat = (points: string, lng: string) => {
  if (lng === "en") {
    return points;
  } else {
    return points?.replaceAll(",", " ");
  }
};
export const getItems = (
  searchQuery: string,
  isSearchPage: boolean,
  hitsItems: any[],
  popularItems: any
) => {
  if (!searchQuery && isSearchPage) return popularItems || [];
  if (searchQuery && isSearchPage) return hitsItems;
  if (!searchQuery && !isSearchPage) return hitsItems;
  return [];
};
export const formatDateRange = (startDate: Date, endDate: Date) => {
  // Create formatter for Canadian Eastern Time
  const options: Intl.DateTimeFormatOptions = {
    month: "long",
    day: "2-digit",
    timeZone: process.env.NEXT_PUBLIC_TIME_ZONE || "America/Toronto", // Canadian Eastern Time
  };

  const formatter = new Intl.DateTimeFormat("en-CA", options);

  // Format start and end date parts
  const startParts = formatter.formatToParts(startDate);
  const endParts = formatter.formatToParts(endDate);
  // Extract month and day from formatted parts
  const startMonth =
    startParts.find((part) => part.type === "month")?.value || "";
  const startDay = startParts.find((part) => part.type === "day")?.value || "";
  const endMonth = endParts.find((part) => part.type === "month")?.value || "";
  const endDay = endParts.find((part) => part.type === "day")?.value || "";

  // Add ordinal suffix to days
  const getOrdinalSuffix = (day: string) => {
    // Convert from "01" format to number
    const num = parseInt(day, 10);

    // Handle special cases for 11, 12, 13
    if (num >= 11 && num <= 13) {
      return `${num}th`;
    }

    // Handle regular cases
    switch (num % 10) {
      case 1:
        return `${num}st`;
      case 2:
        return `${num}nd`;
      case 3:
        return `${num}rd`;
      default:
        return `${num}th`;
    }
  };

  const startDayWithSuffix = getOrdinalSuffix(startDay);
  const endDayWithSuffix = getOrdinalSuffix(endDay);

  return `${startMonth} ${startDayWithSuffix} - ${endMonth} ${endDayWithSuffix}`;
};

export const stripHtmlTags = (input?: string | null): string => {
  return input ? input.replace(/<[^>]*>?/gm, "") : "";
};

// Simple deep comparison function
export const deepEqual = (a: any, b: any): boolean => {
  return JSON.stringify(a) === JSON.stringify(b);
};

export const getPreviousThursdayAndNextWednesday = () => {
  const today = new Date();

  // Get the previous Thursday
  const prevThursday = new Date(today);
  prevThursday.setDate(today.getDate() - ((today.getDay() - 4 + 7) % 7 || 7));
  prevThursday.setHours(0, 0, 0, 0);

  // Get the next Wednesday
  const nextWednesday = new Date(today);
  nextWednesday.setDate(today.getDate() + ((3 - today.getDay() + 7) % 7 || 7));
  nextWednesday.setHours(0, 0, 0, 0);

  return {
    startDate: prevThursday.getTime(),
    endDate: nextWednesday.getTime(),
  };
};

export const handleEmailLogin = async (
  hashValue: string,
  isHash: boolean,
  token?: any
) => {
  try {
    const validateMemberRes = await mulesoftValidateMember({
      key: "validateMember",
      emailOfferUniqueIdentifier: hashValue,
      token: !isHash ? token : "",
      isHash: isHash,
    });
    if (
      validateMemberRes?.response &&
      validateMemberRes?.response?.cardNumber
    ) {
      return validateMemberRes?.response;
    }
  } catch (error: any) {
    console.error("Error fetching Mulesoft validate member:", error);
  }
};